import React, { useState, useCallback, memo } from "react";
import { connect } from "react-redux";
import { RootState } from "../../models/RootState";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import {
  EventData,
  MatchOdds,
  Runners,
} from "../../models/EventData";
import "./MatchOddsTable.scss";
import MarketOddBtn from "../MarketOddBtn/marketOddBtn";
import { BsData, UserBet } from "../../models/ExchBetSlip";
import { addExchangeBet } from "../../store/exchBetSlip/exchBetSlipAction";
import ExchBetSlip from "../ExchBetSlip/ExchBetSlip";
import { ExpandLessSharp } from "@mui/icons-material";
import { placeBet, getBetStatus } from "../../utils/bettingUtils";
import { useDispatch } from "react-redux";
import { clearExchangeBets } from "../../store/exchBetSlip/exchBetSlipReducer";
import { useCashoutLogic, RiskRow } from "../../hooks/useCashoutLogic";
import CashoutDialog from "../Cashout/CashoutDialog";
import TurboCashoutDialog from "../Cashout/TurboCashoutDialog";

type StoreProps = {
  eventMarkets: MatchOdds[];
  eventData?: EventData;
  bets: BsData[];
  addExchangeBet: (data: BsData) => void;
  openBetsData: UserBet[];
  isOneClickBettingEnabled: boolean;
  bettingInprogress: boolean;
  turboCashoutCommission: number;
};

const MatchOddsTable: React.FC<StoreProps> = (props) => {
  const { eventMarkets, bets, eventData, openBetsData, addExchangeBet, isOneClickBettingEnabled, bettingInprogress, turboCashoutCommission } = props;
  const dispatch = useDispatch();

  const [selectedRow, setSelectedRow] = useState<string>("");
  const [coMarket, setCoMarket] = useState<MatchOdds | null>(null);
  const [confirmCashout, setConfirmCashout] = useState<boolean>(false);
  const [confirmTurboCashout, setConfirmTurboCashout] = useState<boolean>(false);
  const disabledStatus = ["suspended", "closed", "suspended-manually"];

  // Use shared cashout logic
  const {
    getTotalPL,
    getOpenBetsPLInArray,
    isHaveCashOut,
    calculateCashout,
    getCashoutProfit,
    getTurboCashoutData,
    turboCashout,
    isTurboCashoutAvailable,
    exposureMaps,
  } = useCashoutLogic(eventData?.sportId || "", eventMarkets, openBetsData, "MATCH_ODDS", turboCashoutCommission);

  // Memoized callback functions to prevent unnecessary re-renders
  const memoizedGetTotalPL = useCallback(
    (marketId: string, runner: Runners) => getTotalPL(marketId, runner, bets),
    [getTotalPL, bets]
  );

  const autoCashout = async () => {
    if (coMarket == null || !eventData) return;

    const response = calculateCashout(coMarket);
    if (!response || response.oddValue == 0) return;

    // Create BsData object with all required fields like addExchangeBet
    const betData: BsData = {
      eventId: eventData.eventId,
      eventName: eventData.eventName,
      marketId: coMarket.marketId,
      marketName: coMarket.marketName,
      marketType: "MATCH_ODDS",
      runnerId: response.runnerId,
      runnerName: response.runnerName,
      oddValue: +response.oddValue.toFixed(2),
      betType: response.betType as "BACK" | "LAY",
      stakeAmount: +(response.stake).toFixed(2),
      competitionId: eventData.competitionId,
      sportId: eventData.sportId,
      minLimit: coMarket?.limits?.minBetValue || 100,
      maxLimit: coMarket?.limits?.maxBetValue || 5000,
      provider: eventData?.markets?.matchOddsProvider || "",
      origin: "EXCHANGE"
    };

    // Use the same bet placement flow as addExchangeBet
    // Clear existing bets first
    dispatch(clearExchangeBets({}));

    // Place the bet directly (similar to one-click betting flow)
    const result = await placeBet(betData, dispatch, (betData: BsData) => getBetStatus(betData, dispatch));
    if (result) {
      setConfirmCashout(false);
      setCoMarket(null);
    }
  };

  const handleTurboCashout = async () => {
    if (coMarket == null || !eventData) return;
    const provider = eventData?.markets?.matchOddsProvider || "";
    try {
      await turboCashout(coMarket, eventData.sportId, eventData.eventId, provider, dispatch);
    } catch (err) {
      console.error("Turbo cashout failed:", err);
    } finally {
      // always run, success or error
      setConfirmTurboCashout(false);
      setCoMarket(null);
    }
  };

  return (
    <>
      <div className="matchodds-table-ctn">
        <div className="matchodds-table-content table-ctn">
          {eventMarkets?.length > 0
            ? eventMarkets.map((market, index) => {
              return (
                <>
                  <Accordion
                    defaultExpanded={true}
                    className="markets-accordian">
                    <AccordionSummary
                      expandIcon={
                        <ExpandLessSharp className="expand-icon" />
                      }
                      aria-controls="panel1a-content"
                    >
                      <TableRow
                        className="acc-head"
                      >
                        <TableCell
                          colSpan={3}
                          className="acc-cell"
                        >
                          <div className="market-name-cell-head-ctn">
                            <div className="market-name-section">
                              <div className="market-name">
                                {market.marketName}
                              </div>
                              <div className="cashout-option">
                                {/* {isHaveCashOut(market, disabledStatus) && (
                                  <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    className="btn cashout-btn"
                                    disabled={
                                      disabledStatus.includes(
                                        market.status?.toLowerCase() || ''
                                      ) ||
                                      getCashoutProfit(market) ==
                                      Infinity ||
                                      bettingInprogress
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCoMarket(market);
                                      setConfirmCashout(true);
                                    }}
                                  >
                                    Cashout{' '}
                                    {getCashoutProfit(market) != Infinity
                                      ? `: ${getCashoutProfit(market)}`
                                      : ''}
                                  </Button>
                                )}
                                {isTurboCashoutAvailable(market, exposureMaps as Record<string, RiskRow[]>) && (
                                  <Button
                                    size="small"
                                    color="secondary"
                                    variant="contained"
                                    className="btn cashout-btn"
                                    disabled={
                                      disabledStatus.includes(
                                        market.status?.toLowerCase() || ''
                                      ) ||
                                      bettingInprogress
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCoMarket(market);
                                      setConfirmTurboCashout(true);
                                    }}
                                  >
                                    Turbo Cashout
                                  </Button>
                                )} */}
                              </div>
                            </div>
                            <span className="bet-limits-section mob-view">
                              <div>
                                MIN:{market?.limits?.minBetValue
                                  ? market?.limits?.minBetValue
                                  : "100"}&nbsp;
                              </div>
                              <div>
                                MAX:{market?.limits?.maxBetValue
                                  ? market?.limits?.maxBetValue %
                                    1000 ==
                                    0
                                    ? market?.limits?.maxBetValue /
                                    1000 +
                                    "K"
                                    : market?.limits?.maxBetValue
                                  : "5K"}
                              </div>
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer
                        key={index}
                        component={Paper}
                      >
                        <Table className="matchodds-table">
                          <TableBody>
                            <TableRow className="header-row">
                              <TableCell colSpan={1} align="left" className="market-name-cell-head">
                                <div className="teamname-odd">Market</div>
                              </TableCell>
                              <TableCell colSpan={1} align="right" className="odds-cell-head">
                                <div className="back-odd">
                                  BACK
                                </div>
                              </TableCell>
                              <TableCell colSpan={1} align="left" className="odds-cell-head">
                                <div className="lay-odd">
                                  LAY
                                </div>
                              </TableCell>
                            </TableRow>
                            {market.runners?.length > 0 ? (
                              <>
                                {market.runners.map((runner) => (
                                  <MatchOddsRow
                                    key={`${market.marketId}-${runner.runnerId}`}
                                    isOneClickBettingEnabled={isOneClickBettingEnabled}
                                    getOpenBetsPL={getOpenBetsPLInArray}
                                    getTotalPL={memoizedGetTotalPL}
                                    runner={runner}
                                    disabledStatus={disabledStatus}
                                    eventData={eventData!}
                                    marketName={market.marketName}
                                    marketId={market.marketId}
                                    selectedRow={selectedRow}
                                    setSelectedRow={setSelectedRow}
                                    bets={bets}
                                    addExchangeBet={addExchangeBet}
                                    min={
                                      market?.limits?.minBetValue
                                        ? market?.limits?.minBetValue
                                        : 0
                                    }
                                    max={
                                      market?.limits?.maxBetValue
                                        ? market?.limits?.maxBetValue
                                        : 0
                                    }
                                    oddsLimit={
                                      market?.limits?.oddsLimit
                                        ? market?.limits?.oddsLimit
                                        : 0
                                    }
                                  />
                                ))}
                              </>
                            ) : (
                              <>
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <div className="fm-table-msg-text">
                                      No Match Odds for this event
                                    </div>
                                  </TableCell>
                                </TableRow>
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                </>
              );
            })
            : null}
        </div>
      </div>
      <CashoutDialog
        open={confirmCashout}
        onClose={() => setConfirmCashout(false)}
        onConfirm={autoCashout}
        bettingInprogress={bettingInprogress}
        cashoutAmount={coMarket ? getCashoutProfit(coMarket) : undefined}
      />
      <TurboCashoutDialog
        open={confirmTurboCashout}
        onClose={() => setConfirmTurboCashout(false)}
        onConfirm={handleTurboCashout}
        loading={bettingInprogress}
        market={coMarket}
        getTurboCashoutData={getTurboCashoutData}
        exposureMaps={exposureMaps as Record<string, RiskRow[]>}
        turboCashoutCommission={turboCashoutCommission}
      />
    </>
  );
};

type MatchOddsRowProps = {
  getOpenBetsPL: (
    marketId: string,
    runner: Runners
  ) => any;
  getTotalPL: (
    marketId: string,
    runner: Runners
  ) => any;
  runner: Runners;
  disabledStatus: string[];
  eventData: EventData;
  marketName: string;
  selectedRow: string;
  marketId: string;
  setSelectedRow: (data: any) => void;
  min: number;
  max: number;
  oddsLimit: number;
  bets: BsData[];
  addExchangeBet: (data: BsData) => void;
  isOneClickBettingEnabled: boolean;
};

const MatchOddsRow: React.FC<MatchOddsRowProps> = memo((props) => {
  const {
    getOpenBetsPL,
    getTotalPL,
    runner: originalRunner,
    marketId,
    disabledStatus,
    eventData,
    marketName,
    selectedRow,
    setSelectedRow,
    min,
    max,
    oddsLimit,
    addExchangeBet,
    bets,
    isOneClickBettingEnabled,
  } = props;

  const runner = {
    ...originalRunner,
    backPrices:
      originalRunner?.backPrices === null
        ? [{ price: 0, size: 0 }, { price: 0, size: 0 }, { price: 0, size: 0 }]
        : [
          ...originalRunner.backPrices,
          ...Array(Math.max(0, 3 - originalRunner.backPrices.length)).fill({
            price: 0,
            size: 0,
          }),
        ],
    layPrices:
      originalRunner?.layPrices === null
        ? [{ price: 0, size: 0 }, { price: 0, size: 0 }, { price: 0, size: 0 }]
        : [
          ...originalRunner.layPrices,
          ...Array(Math.max(0, 3 - originalRunner.layPrices.length)).fill({
            price: 0,
            size: 0,
          }),
        ],
  };

  return (
    <>
      <TableRow className="table-row-margin">
        <TableCell className="team-name-cell">
          <div className="team">{runner.runnerName}
            {getOpenBetsPL(
              marketId,
              runner
            ).map((ret: any) =>
              ret !== null ? (
                <span className={ret >= 0 ? 'profit' : 'loss'}>
                  {ret > 0
                    ? '+' + Number(ret).toFixed(2)
                    : Number(ret).toFixed(2)}
                </span>
              ) : null
            )}
          </div>
          {getTotalPL(
            marketId,
            runner
          ).map((ret: any) =>
            ret !== null ? (
              <div className="profit-loss-box">
                <span className={ret >= 0 ? 'profit' : 'loss'}>
                  {ret > 0
                    ? '+' + Number(ret).toFixed(2)
                    : Number(ret).toFixed(2)}
                </span>
              </div>
            ) : null
          )}
        </TableCell>

        <TableCell className="odds-cell">
          <div className="odds-block web-view back-odds-block">
            {runner?.backPrices?.length > 0 ? (
              runner?.backPrices?.map((odds, idx) => (
                <MarketOddBtn
                  mainValue={odds.price ? odds.price : 0}
                  subValue={odds.size}
                  oddType="back-odd"
                  valueType="matchOdds"
                  showSubValueinKformat={true}
                  oddsSet={[
                    runner.backPrices && runner.backPrices[0]
                      ? runner.backPrices[0].price
                      : 0,
                  ]}
                  key={idx}
                  disable={
                    (disabledStatus.includes(eventData?.status?.toLowerCase()) ||
                      eventData?.status !== "IN_PLAY")
                  }
                  onClick={() => {
                    if (eventData?.status === "IN_PLAY") {
                      addExchangeBet({
                        eventId: eventData?.eventId,
                        eventName: eventData?.eventName,
                        marketId: marketId,
                        marketName: marketName,
                        oddValue: odds.price,
                        betType: "BACK",
                        marketType: "MATCH_ODDS",
                        stakeAmount: 0,
                        runnerId: runner.runnerId,
                        runnerName: runner.runnerName,
                        competitionId: eventData?.competitionId,
                        sportId: eventData?.sportId,
                        maxLimit: max,
                        minLimit: min,
                        provider: eventData?.markets?.matchOddsProvider || ""
                      });
                    }
                  }}
                />
              ))
            ) : (
              <></>
            )}
          </div>
          <div className="odds-block mob-view" style={{ marginRight: "3px" }}>
            <MarketOddBtn
              mainValue={
                runner.backPrices && runner.backPrices[0]
                  ? runner.backPrices[0].price
                  : 0
              }
              subValue={
                runner.backPrices && runner.backPrices[0]
                  ? runner.backPrices[0].size
                  : 0
              }
              oddType="back-odd"
              valueType="matchOdds"
              showSubValueinKformat={true}
              disable={
                (disabledStatus.includes(eventData?.status?.toLowerCase()) ||
                  eventData?.status !== "IN_PLAY")
              }
              onClick={() => {
                if (eventData?.status === "IN_PLAY") {
                  addExchangeBet({
                    eventId: eventData?.eventId,
                    eventName: eventData?.eventName,
                    marketId: marketId,
                    marketName: marketName,
                    marketType: "MATCH_ODDS",
                    oddValue:
                      runner.backPrices && runner.backPrices[0]
                        ? runner.backPrices[0].price
                        : 0,
                    betType: "BACK",
                    stakeAmount: 0,
                    runnerId: runner.runnerId,
                    runnerName: runner.runnerName,
                    competitionId: eventData?.competitionId,
                    sportId: eventData?.sportId,
                    maxLimit: max,
                    minLimit: min,
                    provider: eventData?.markets?.matchOddsProvider || ""
                  });
                  setSelectedRow(
                    marketId + runner.runnerId + marketName + "MO"
                  );
                }
              }}
            />
          </div>
        </TableCell>
        <TableCell className="odds-cell w-17 mobile-pr-20">
          <div className="odds-block web-view">
            {runner?.layPrices?.length > 0 ? (
              runner?.layPrices?.map((odds, idx) => (
                <MarketOddBtn
                  mainValue={odds.price ? odds.price : 0}
                  subValue={odds.size ? odds.size : 0}
                  oddType="lay-odd"
                  valueType="matchOdds"
                  showSubValueinKformat={true}
                  oddsSet={[
                    runner.layPrices && runner.layPrices[0]
                      ? runner.layPrices[0].price
                      : 0,
                  ]}
                  key={idx}
                  disable={
                    (disabledStatus.includes(eventData?.status?.toLowerCase()) ||
                      eventData?.status !== "IN_PLAY")
                  }
                  onClick={() => {
                    if (eventData?.status === "IN_PLAY")
                      addExchangeBet({
                        eventId: eventData?.eventId,
                        eventName: eventData?.eventName,
                        marketId: marketId,
                        marketName: marketName,
                        marketType: "MATCH_ODDS",
                        oddValue: odds.price,
                        betType: "LAY",
                        stakeAmount: 0,
                        runnerId: runner.runnerId,
                        runnerName: runner.runnerName,
                        competitionId: eventData?.competitionId,
                        sportId: eventData?.sportId,
                        maxLimit: max,
                        minLimit: min,
                        provider: eventData?.markets?.matchOddsProvider || ""
                      });
                  }}
                />
              ))
            ) : (
              <></>
            )}
          </div>
          <div className="odds-block mob-view">
            <MarketOddBtn
              mainValue={
                runner.layPrices && runner.layPrices[0]
                  ? runner.layPrices[0].price
                  : 0
              }
              subValue={
                runner.layPrices && runner.layPrices[0]
                  ? runner.layPrices[0].size
                  : 0
              }
              oddType="lay-odd"
              valueType="matchOdds"
              showSubValueinKformat={true}
              disable={
                (disabledStatus.includes(eventData?.status?.toLowerCase()) ||
                  eventData?.status !== "IN_PLAY")
              }
              onClick={() => {
                if (eventData?.status === "IN_PLAY")
                  addExchangeBet({
                    eventId: eventData?.eventId,
                    eventName: eventData?.eventName,
                    marketId: marketId,
                    marketName: marketName,
                    marketType: "MATCH_ODDS",
                    oddValue:
                      runner.layPrices && runner.layPrices[0]
                        ? runner.layPrices[0].price
                        : 0,
                    betType: "LAY",
                    stakeAmount: 0,
                    runnerId: runner.runnerId,
                    runnerName: runner.runnerName,
                    competitionId: eventData?.competitionId,
                    sportId: eventData?.sportId,
                    maxLimit: max,
                    minLimit: min,
                    provider: eventData?.markets?.matchOddsProvider || ""
                  });

                setSelectedRow(marketId + runner.runnerId + marketName + "MO");
              }}
            />
          </div>
        </TableCell>
      </TableRow>
      {!isOneClickBettingEnabled && bets?.length > 0 &&
        bets?.[0]?.marketName === marketName &&
        bets?.[0]?.marketId === marketId &&
        bets?.[0]?.runnerId === runner.runnerId &&
        <TableRow className="inline-betslip">
          <TableCell colSpan={3}>
            {' '}
            <ExchBetSlip balance={0} exposure={0} />{' '}
          </TableCell>
        </TableRow>
      }
    </>
  );
});

const mapStateToProps = (state: RootState) => {
  return {
    eventData: state.exchangeSports.eventInfo,
    openBetsData: state.exchBetSlip.openBets,
    bets: state.exchBetSlip.bets,
    isOneClickBettingEnabled: state.exchBetSlip.isOneClickBettingEnabled,
    bettingInprogress: state.exchBetSlip.bettingInprogress,
    turboCashoutCommission: state.exchangeSports.turboCashoutCommission,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    addExchangeBet: (data: BsData) => dispatch(addExchangeBet(data)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MatchOddsTable);
